import React, {useEffect, useRef, useState} from "react";
import { useFormik } from "formik";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Textarea,
  VStack,
  useToast,
  Container,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Text,
  HStack,
  SimpleGrid,
  GridItem,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaEnvelope, FaPaperPlane } from "react-icons/fa";
import * as Yup from 'yup';
import useSubmit from "../../hooks/useSubmit";
import {useAlertContext} from "../../context/alertContext";
import { motion } from "framer-motion";
import ScrollAnimationWrapper from "../ScrollAnimationWrapper";

const MotionBox = motion(Box);

const ContactMeSection = () => {
  const {isLoading, submit} = useSubmit();
  const { response } = useAlertContext();
  const toast = useToast();
  const menuButtonRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState(0);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      email: "",
      type: "hireMe",
      budget: "",
      comment: "",
    },
    onSubmit: (values) => {
      submit("https://alwialhaddad.com/", values);
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Required"),
      type: Yup.string(),
      budget: Yup.string(),
      comment: Yup.string()
        .min(25, "Must be at least 25 characters")
        .required("Required"),
    }),
    validateOnMount: true,
    validateOnChange: true,
  });
  
  useEffect(() => {
    if (response) {
      if (response.type === "success") {
        formik.resetForm();
      }
    }
  }, [response, formik]);

  useEffect(() => {
    if (menuButtonRef.current) {
      setMenuWidth(menuButtonRef.current.offsetWidth);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    formik.handleSubmit(e);
    
    if (Object.keys(formik.errors).length > 0) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const enquiryTypes = [
    { value: "webDev", label: "Web Development Project" },
    { value: "mlAi", label: "Machine Learning / AI Consultation" },
    { value: "collab", label: "Collaboration Opportunity" },
    { value: "jobOffer", label: "Job Opportunity" },
    { value: "other", label: "Other" },
  ];

  const getEnquiryLabel = (value) => {
    const selectedType = enquiryTypes.find(type => type.value === value);
    return selectedType ? selectedType.label : "Select type";
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.5, 
        delay: custom * 0.1,
        ease: "easeOut"
      },
    }),
  };

  return (
    <Box
      as="section"
      id="contactme-section"
      position="relative"
      py={{ base: "4rem", md: "6rem" }}
      bg="brand.primary"
      overflow="hidden"
      borderTop="1px solid"
      borderColor="rgba(255, 255, 255, 0.05)"
    >
      {/* Background decorative elements */}
      <Box
        position="absolute"
        top="10%"
        left="-5%"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="rgba(79, 209, 197, 0.05)"
        filter="blur(70px)"
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom="-10%"
        right="-5%"
        w="350px"
        h="350px"
        borderRadius="full"
        bg="rgba(49, 130, 206, 0.07)"
        filter="blur(80px)"
        zIndex={0}
      />

      <Container maxW="1200px" zIndex={1} position="relative">
        <SimpleGrid 
          columns={{ base: 1, md: 2 }} 
          spacing={{ base: 10, md: 16 }}
          alignItems="center"
        >
          {/* Left column - Contact Info */}
          <GridItem>
            <VStack align="flex-start" spacing={8}>
              <ScrollAnimationWrapper>
                <Text
                  color="brand.accent"
                  fontWeight="600"
                  letterSpacing="1px"
                  textTransform="uppercase"
                  fontSize="sm"
                  mb={2}
                >
                  Get in Touch
                </Text>
                <Heading
                  as="h2"
                  fontSize={{ base: "3xl", md: "4xl" }}
                  fontWeight="bold"
                  lineHeight="1.2"
                  mb={4}
                >
                  Let's work together
                </Heading>
                <Text
                  color="brand.text.secondary"
                  fontSize="lg"
                  lineHeight="1.7"
                  maxW="500px"
                >
                  Have a project in mind or want to explore collaboration opportunities? 
                  I'm always open to discussing new ideas and challenges.
                </Text>
              </ScrollAnimationWrapper>

              <MotionBox
                initial="hidden"
                animate="visible"
                custom={1}
                variants={fadeInUp}
                p={6}
                borderRadius="xl"
                bg="brand.bg.card"
                boxShadow="md"
                backdropFilter="blur(10px)"
                border="1px solid"
                borderColor="whiteAlpha.200"
                w="100%"
              >
                <VStack align="flex-start" spacing={4}>
                  <Heading as="h3" fontSize="xl" mb={2}>
                    How I can help
                  </Heading>
                  <Text color="brand.text.secondary">
                    I offer expertise in full-stack development, machine learning solutions, and UI/UX design.
                    Let's bring your vision to life with quality code and elegant design.
                  </Text>
                  <HStack mt={4} spacing={4}>
                    <Button
                      as="a"
                      href="mailto:syedalwialhaddad@gmail.com"
                      variant="outline"
                      leftIcon={<Icon as={FaEnvelope} />}
                      colorScheme="blue"
                      _hover={{
                        bg: "whiteAlpha.100",
                        transform: "translateY(-2px)",
                      }}
                    >
                      syedalwialhaddad@gmail.com
                    </Button>
                  </HStack>
                </VStack>
              </MotionBox>
            </VStack>
          </GridItem>

          {/* Right column - Contact Form */}
          <GridItem>
            <ScrollAnimationWrapper delay={0.2} distance={40}>
              <Box
                bg="brand.bg.card"
                p={{ base: 6, md: 8 }}
                borderRadius="xl"
                boxShadow="xl"
                backdropFilter="blur(10px)"
              >
                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <VStack spacing={6} w="100%">
                    <FormControl 
                      isInvalid={formik.touched.firstName && !!formik.errors.firstName}
                      isRequired
                    >
                      <FormLabel htmlFor="firstName" fontWeight="medium">
                        Your Name
                      </FormLabel>                      <Input
                        id="firstName"
                        name="firstName"
                        {...formik.getFieldProps("firstName")}
                        bg="whiteAlpha.100"
                        color="white"
                        borderRadius="md"
                        borderColor="whiteAlpha.300"
                        _placeholder={{ color: 'whiteAlpha.500' }}
                        _hover={{ borderColor: "brand.accent" }}
                        _focus={{ 
                          borderColor: "brand.accent", 
                          boxShadow: "0 0 0 1px rgba(246, 173, 85, 0.6)" 
                        }}
                        size="lg"
                        placeholder="Enter your name"
                        style={{ color: "white" }}
                      />
                      <FormErrorMessage>{formik.errors.firstName}</FormErrorMessage>
                    </FormControl>

                    <FormControl 
                      isInvalid={formik.touched.email && !!formik.errors.email}
                      isRequired
                    >
                      <FormLabel htmlFor="email" fontWeight="medium">
                        Email Address
                      </FormLabel>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        {...formik.getFieldProps("email")}
                        bg="whiteAlpha.100"
                        color="white"
                        borderRadius="md"
                        borderColor="whiteAlpha.300"
                        _placeholder={{ color: 'whiteAlpha.500' }}
                        _hover={{ borderColor: "brand.accent" }}
                        _focus={{ 
                          borderColor: "brand.accent", 
                          boxShadow: "0 0 0 1px rgba(246, 173, 85, 0.6)"
                        }}
                        size="lg"
                        placeholder="Enter your email"
                        style={{ color: "white" }}
                      />
                      <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel htmlFor="type" fontWeight="medium">
                        Type of Enquiry
                      </FormLabel>
                      <Menu width="100%">
                        <MenuButton
                          ref={menuButtonRef}
                          as={Button}
                          w="100%"
                          textAlign="left"
                          fontWeight="normal"
                          rightIcon={<ChevronDownIcon />}
                          bg="whiteAlpha.100"
                          color="white"
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor="whiteAlpha.300"
                          _hover={{ borderColor: "brand.accent" }}
                          _active={{ bg: "whiteAlpha.200" }}
                          size="lg"
                        >
                          {getEnquiryLabel(formik.values.type)}
                        </MenuButton>
                        <MenuList 
                          bg="rgba(26, 32, 44, 0.95)"
                          borderColor="whiteAlpha.300"
                          backdropFilter="blur(10px)"
                          width={menuWidth ? `${menuWidth}px` : 'auto'}
                          boxShadow="xl"
                          zIndex="1001"
                        >
                          {enquiryTypes.map((type) => (
                            <MenuItem 
                              key={type.value} 
                              onClick={() => formik.setFieldValue("type", type.value)}
                              bg={type.value === formik.values.type ? "whiteAlpha.200" : "transparent"}
                              color="white"
                              _hover={{ bg: "whiteAlpha.200" }} 
                              _focus={{ bg: "whiteAlpha.300" }} 
                              _active={{ bg: "whiteAlpha.300" }}
                            >
                              {type.label}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    </FormControl>

                    <FormControl>
                      <FormLabel htmlFor="budget" fontWeight="medium">
                        Budget Range
                      </FormLabel>
                      <Input
                        id="budget"
                        name="budget"
                        {...formik.getFieldProps("budget")}
                        bg="whiteAlpha.100"
                        color="white"
                        borderRadius="md"
                        borderColor="whiteAlpha.300"
                        _placeholder={{ color: 'whiteAlpha.500' }}
                        _hover={{ borderColor: "brand.accent" }}
                        _focus={{ 
                          borderColor: "brand.accent",
                          boxShadow: "0 0 0 1px rgba(246, 173, 85, 0.6)"
                        }}
                        size="lg"
                        placeholder="Enter your budget ($300+)"
                        style={{ color: "white" }}
                      />
                    </FormControl>

                    <FormControl
                      isInvalid={formik.touched.comment && !!formik.errors.comment}
                      isRequired
                    >
                      <FormLabel htmlFor="comment" fontWeight="medium">
                        Your Message
                      </FormLabel>
                      <Textarea
                        id="comment"
                        name="comment"
                        height={150}
                        {...formik.getFieldProps("comment")}
                        bg="whiteAlpha.100"
                        color="white"
                        borderRadius="md"
                        borderColor="whiteAlpha.300"
                        _placeholder={{ color: 'whiteAlpha.500' }}
                        _hover={{ borderColor: "brand.accent" }}
                        _focus={{ 
                          borderColor: "brand.accent", 
                          boxShadow: "0 0 0 1px rgba(246, 173, 85, 0.6)"
                        }}
                        size="lg"
                        placeholder="Tell me about your project or inquiry"
                        resize="vertical"
                        style={{ color: "white" }}
                      />
                      <FormErrorMessage>{formik.errors.comment}</FormErrorMessage>
                    </FormControl>

                    <Button 
                      type="submit" 
                      colorScheme="blue" 
                      width="full"
                      isLoading={isLoading}
                      loadingText="Submitting..."
                      size="lg"
                      height="56px"
                      rightIcon={<Icon as={FaPaperPlane} />}
                      _hover={{
                        transform: "translateY(-3px)",
                        boxShadow: "lg",
                      }}
                      fontSize="md"
                      fontWeight="medium"
                      mt={2}
                    >
                      Send Message
                    </Button>
                  </VStack>
                </form>
              </Box>
            </ScrollAnimationWrapper>
          </GridItem>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

ContactMeSection.propTypes = {
  // Add any props if needed in the future
};

export default ContactMeSection;
